import { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createMarketplaceListing,
  updateMarketplaceListing,
  getMarketplaceCategories,
  uploadMarketplaceImage,
  getPublicImageUrl,
  type MarketplaceListing,
  getMarketplaceListing
} from "@/lib/marketplaceAPI";
import { type TechnicalSpec } from "@/lib/schema";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  ImagePlus,
  Loader2,
  Plus,
  Tag,
  X,
} from "lucide-react";

interface AdminUsedProductFormProps {
  productId: string | null;
  onComplete: () => void;
}

interface LocalImage {
  file: File;
  preview: string;
}

// Definimos un tipo para nuestros datos
interface ProductData {
  id?: string;
  title: string;
  description: string;
  brand: string;
  model?: string;
  condition: string;
  price: number;
  location: string;
  year?: number;
  category_id: string;
  features?: string[];
  technical_specs?: TechnicalSpec[];
  images?: string[];
  published_at?: string;
  is_active: boolean;
}

export default function AdminUsedProductForm({ productId, onComplete }: AdminUsedProductFormProps) {
  const { toast } = useToast();
  const [currentListing, setCurrentListing] = useState<ProductData>(() => {
    if (productId) {
      return {
        id: productId,
        title: '',
        description: '',
        brand: '',
        model: '',
        condition: 'excellent',
        price: 0,
        location: '',
        year: undefined,
        category_id: '',
        features: [],
        technical_specs: [],
        images: [],
        is_active: true
      };
    }
    return {
      title: '',
      description: '',
      brand: '',
      model: '',
      condition: 'excellent',
      price: 0,
      location: '',
      year: undefined,
      category_id: '',
      features: [],
      technical_specs: [],
      images: [],
      is_active: true
    };
  });
  const [localImages, setLocalImages] = useState<LocalImage[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  // Fetch categories for the dropdown
  const { data: categories, isLoading: isLoadingCategories } = useQuery({
    queryKey: ["marketplace-categories"],
    queryFn: getMarketplaceCategories,
  });

  // Fetch product details if editing
  const { data: fetchedProductData, isLoading: isLoadingProduct } = useQuery<MarketplaceListing | null>({
    queryKey: ["marketplace-listing", productId],
    queryFn: () => (productId ? getMarketplaceListing(productId) : null),
    enabled: !!productId, // Solo ejecutar si productId existe
    retry: false, // No reintentar si falla la carga inicial (opcional)
  });

  // Update form when product data is loaded
  useEffect(() => {
    if (fetchedProductData) {
      // Adaptar fetchedProductData a la estructura de ProductData si es necesario
      // Por ejemplo, category_id podría necesitar ser un string
      setCurrentListing({
        ...fetchedProductData,
        category_id: String(fetchedProductData.category_id), // Asegurar que category_id sea string
        // Asegurar que todos los campos opcionales tengan un valor por defecto si no vienen
        features: fetchedProductData.features || [],
        technical_specs: fetchedProductData.technical_specs || [],
        images: fetchedProductData.images || [],
        year: fetchedProductData.year === null ? undefined : fetchedProductData.year, // Manejar posible null para year
        model: fetchedProductData.model || '', // Añadir model si existe en MarketplaceListing
      });
      // Si las imágenes vienen como rutas y no URLs completas, convertirlas
      // Esto depende de cómo se guarden y recuperen las imágenes
      // Si `fetchedProductData.images` ya son URLs completas, no se necesita lo siguiente:
      /*
      const imageUrls = (fetchedProductData.images || []).map(imgPath =>
        imgPath.startsWith('http') ? imgPath : getPublicImageUrl(imgPath)
      );
      setCurrentListing(prev => ({ ...prev, images: imageUrls }));
      */

      // También podrías querer limpiar localImages aquí si estás editando
      // y las imágenes existentes se manejan a través de currentListing.images
      setLocalImages([]);

    } else if (!productId) {
      // Si no hay productId (creación), asegurar que el formulario esté reseteado a valores iniciales
      setCurrentListing({
        title: '',
        description: '',
        brand: '',
        model: '',
        condition: 'excellent',
        price: 0,
        location: '',
        year: undefined,
        category_id: '',
        features: [],
        technical_specs: [],
        images: [],
        is_active: true
      });
      setLocalImages([]);
    }
  }, [fetchedProductData, productId]);

  // Setup dropzone for image uploads
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxSize: 5 * 1024 * 1024, // 5MB
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        rejectedFiles.forEach(file => {
          if (file.errors?.some(e => e.code === 'file-too-large')) {
            toast({
              title: "Archivo demasiado grande",
              description: `El archivo ${file.file.name} supera el límite de 5MB`,
              variant: "destructive"
            });
          } else {
            toast({
              title: "Formato no válido",
              description: "Solo se permiten imágenes (JPEG, PNG, GIF, WEBP)",
              variant: "destructive"
            });
          }
        });
      }

      const newImages = acceptedFiles.map(file => ({
        file,
        preview: URL.createObjectURL(file)
      }));

      setLocalImages(prev => [...prev, ...newImages]);
    }
  });

  // Handle removing local images
  const handleRemoveLocalImage = (index: number) => {
    const newImages = [...localImages];
    // Liberar la URL del objeto para evitar fugas de memoria
    URL.revokeObjectURL(newImages[index].preview);
    newImages.splice(index, 1);
    setLocalImages(newImages);
  };

  // Handle adding features
  const handleAddFeature = () => {
    // Mostrar un prompt para añadir una característica
    const feature = prompt("Introduce una característica (ej: 'Potencia: 100CV')");
    if (feature) {
      const features = [...(currentListing.features || []), feature];
      setCurrentListing({ ...currentListing, features });
    }
  };

  // Handle removing features
  const handleRemoveFeature = (index: number) => {
    const features = [...(currentListing.features || [])];
    features.splice(index, 1);
    setCurrentListing({ ...currentListing, features });
  };

  // Handle adding technical spec
  const handleAddTechnicalSpec = () => {
    const newSpec: TechnicalSpec = { label: "", value: "" };
    setCurrentListing({
      ...currentListing,
      technical_specs: [...(currentListing.technical_specs || []), newSpec]
    });
  };

  // Handle removing technical spec
  const handleRemoveTechnicalSpec = (index: number) => {
    const specs = [...(currentListing.technical_specs || [])];
    specs.splice(index, 1);
    setCurrentListing({ ...currentListing, technical_specs: specs });
  };

  // Mutations for creating and updating products
  const createMutation = useMutation({
    mutationFn: (data: Omit<MarketplaceListing, 'id' | 'created_at'>) => createMarketplaceListing(data),
    onSuccess: () => {
      toast({
        title: "Producto creado",
        description: "El producto se ha añadido correctamente"
      });
      onComplete();
    },
    onError: (error) => {
      console.error("Error al crear el producto:", error);
      toast({
        title: "Error",
        description: "No se pudo crear el producto. Inténtalo de nuevo.",
        variant: "destructive"
      });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<MarketplaceListing> }) =>
      updateMarketplaceListing(id, data),
    onSuccess: () => {
      toast({
        title: "Producto actualizado",
        description: "El producto se ha actualizado correctamente"
      });
      onComplete();
    },
    onError: (error) => {
      console.error("Error al actualizar el producto:", error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el producto. Inténtalo de nuevo.",
        variant: "destructive"
      });
    }
  });

  // Upload images to Supabase
  const uploadImages = async (): Promise<string[]> => {
    if (localImages.length === 0) {
      return currentListing.images || [];
    }

    setUploadingImages(true);
    const uploadedPaths: string[] = [];
    const currentPaths = [...(currentListing.images || [])];

    try {
      // Upload each image
      for (const image of localImages) {
        try {
          const filePath = await uploadMarketplaceImage(image.file);
          if (filePath) {
            uploadedPaths.push(filePath);
          }
        } catch (error) {
          console.error("Error uploading image:", error);
          toast({
            title: "Error al subir imagen",
            description: "No se pudo subir una imagen",
            variant: "destructive"
          });
        }
      }

      // Combine existing paths with new paths
      return [...currentPaths, ...uploadedPaths];
    } finally {
      setUploadingImages(false);
    }
  };

  // Handle saving the product
  const handleSave = async () => {
    try {
      // Validate required fields
      if (!currentListing.title || currentListing.price === undefined || currentListing.price === null || currentListing.price < 0 || !currentListing.brand ||
          !currentListing.location || !currentListing.category_id || !currentListing.description) {
        toast({
          title: "Campos incompletos o inválidos",
          description: "Por favor, rellena todos los campos obligatorios (*). El precio no puede ser negativo.",
          variant: "destructive"
        });
        return;
      }

      // Upload images first
      const imagePaths = await uploadImages();

      // Prepare data for saving
      const productData = {
        ...currentListing,
        images: imagePaths,
        technical_specs: currentListing.technical_specs || []
      } as Omit<MarketplaceListing, 'id' | 'created_at'>;

      // Create or update
      if (productId) {
        await updateMutation.mutateAsync({
          id: productId,
          data: productData
        });
      } else {
        await createMutation.mutateAsync(productData);
      }

      // Clean up image previews
      localImages.forEach(img => URL.revokeObjectURL(img.preview));

    } catch (error) {
      console.error("Error al guardar:", error);
      setUploadingImages(false);
      toast({
        title: "Error",
        description: "Ocurrió un error al guardar el producto",
        variant: "destructive"
      });
    }
  };

  // Cleanup effect for image previews
  useEffect(() => {
    return () => {
      // Limpiar las URLs de objetos al desmontar
      localImages.forEach(image => URL.revokeObjectURL(image.preview));
    };
  }, []);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="title" className="text-white mb-1.5 block">Título *</Label>
            <Input
              id="title"
              value={currentListing?.title || ''}
              onChange={(e) => setCurrentListing({ ...currentListing, title: e.target.value })}
              placeholder="Ej: Cortadora FAGOR"
              className="bg-[#1A1A1C] border-white/10 focus-visible:ring-[#FCAA0D] text-white"
            />
          </div>

          <div>
            <Label htmlFor="price" className="text-white mb-1.5 block">Precio (€) *</Label>
            <Input
              id="price"
              type="number"
              value={currentListing?.price || ''}
              onChange={(e) => setCurrentListing({ ...currentListing, price: parseFloat(e.target.value) })}
              placeholder="Ej: 12500"
              className="bg-[#1A1A1C] border-white/10 focus-visible:ring-[#FCAA0D] text-white"
            />
          </div>

          <div>
            <Label htmlFor="brand" className="text-white mb-1.5 block">Marca *</Label>
            <Input
              id="brand"
              value={currentListing?.brand || ''}
              onChange={(e) => setCurrentListing({ ...currentListing, brand: e.target.value })}
              placeholder="Ej: FAGOR"
              className="bg-[#1A1A1C] border-white/10 focus-visible:ring-[#FCAA0D] text-white"
            />
          </div>

          <div>
            <Label htmlFor="condition" className="text-white mb-1.5 block">Estado *</Label>
            <Select
              value={currentListing?.condition || 'good'}
              onValueChange={(value) => setCurrentListing({ ...currentListing, condition: value })}
            >
              <SelectTrigger className="bg-[#1A1A1C] border-white/10 focus:ring-[#FCAA0D] text-white">
                <SelectValue placeholder="Selecciona el estado" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1C] border-white/10 text-white">
                <SelectItem value="new" className="focus:bg-white/10">Como nuevo</SelectItem>
                <SelectItem value="excellent" className="focus:bg-white/10">Excelente</SelectItem>
                <SelectItem value="good" className="focus:bg-white/10">Bueno</SelectItem>
                <SelectItem value="fair" className="focus:bg-white/10">Regular</SelectItem>
                <SelectItem value="poor" className="focus:bg-white/10">Necesita reparación</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="location" className="text-white mb-1.5 block">Ubicación *</Label>
            <Input
              id="location"
              value={currentListing?.location || ''}
              onChange={(e) => setCurrentListing({ ...currentListing, location: e.target.value })}
              placeholder="Ej: Barcelona"
              className="bg-[#1A1A1C] border-white/10 focus-visible:ring-[#FCAA0D] text-white"
            />
          </div>

          <div>
            <Label htmlFor="year" className="text-white mb-1.5 block">Año de fabricación</Label>
            <Input
              id="year"
              type="number"
              value={currentListing?.year !== undefined ? currentListing.year : new Date().getFullYear()}
              onChange={(e) => {
                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                setCurrentListing({ ...currentListing, year: value });
              }}
              placeholder="Ej: 2018"
              className="bg-[#1A1A1C] border-white/10 focus-visible:ring-[#FCAA0D] text-white"
            />
          </div>

          <div>
            <Label htmlFor="category" className="text-white mb-1.5 block">Categoría *</Label>
            <Select
              value={currentListing?.category_id || ''}
              onValueChange={(value) => setCurrentListing({ ...currentListing, category_id: value })}
            >
              <SelectTrigger className="bg-[#1A1A1C] border-white/10 focus:ring-[#FCAA0D] text-white">
                <SelectValue placeholder="Selecciona una categoría" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1C] border-white/10 text-white">
                {isLoadingCategories ? (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Cargando...
                  </div>
                ) : (
                  categories?.map((category) => (
                    <SelectItem key={category.id} value={String(category.id)} className="focus:bg-white/10">
                      {category.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={currentListing?.is_active}
              onCheckedChange={(checked) => setCurrentListing({ ...currentListing, is_active: checked })}
              className="data-[state=checked]:bg-[#FCAA0D]"
            />
            <Label htmlFor="is_active" className="text-white">Producto activo</Label>
          </div>
        </div>
      </div>

      {/* Sección de imágenes */}
      <div>
        <Label className="text-white mb-1.5 block">Imágenes</Label>
        <div className="space-y-3">
          {/* Zona para arrastrar y soltar imágenes */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-[#FCAA0D] bg-[#FCAA0D]/5'
                : 'border-white/10 hover:border-[#FCAA0D]/50'
            }`}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center justify-center gap-2 p-4">
              <ImagePlus className="h-10 w-10 text-white/40" />
              <p className="text-sm text-white/60">
                {isDragActive
                  ? "Suelta las imágenes aquí..."
                  : "Arrastra y suelta imágenes aquí, o haz clic para seleccionarlas"
                }
              </p>
              <p className="text-xs text-white/40">
                Formatos permitidos: JPEG, PNG, GIF, WEBP. Máximo 5MB por imagen.
              </p>
            </div>
          </div>

          {/* Vista previa de imágenes locales para subir */}
          {localImages.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-white/80">Imágenes para subir</p>
              <div className="grid grid-cols-3 gap-3">
                {localImages.map((image, index) => (
                  <div key={index} className="relative group">
                    <div className="relative w-full h-24 rounded-md overflow-hidden border border-white/10">
                      <img
                        src={image.preview}
                        alt={`Imagen local ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Si falla la previsualización, mostrar un placeholder
                          e.currentTarget.src = "https://placehold.co/200x200?text=Preview+Error";
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveLocalImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Listado de imágenes actuales */}
          {currentListing?.images && currentListing.images.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-white/80">Imágenes guardadas</p>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {currentListing.images.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={getPublicImageUrl(image)}
                      alt={`Imagen ${index + 1}`}
                      className="w-full h-24 object-cover rounded-md border border-white/10"
                      onError={(e) => {
                        console.error("Error al cargar la imagen:", image);

                        // Intentar con URL alternativa
                        try {
                          let fileName = "";
                          if (image.includes('/')) {
                            const parts = image.split('/');
                            fileName = parts[parts.length - 1];
                          } else {
                            fileName = image;
                          }
                          const newSrc = `https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/marketplace-used/${fileName}`;
                          e.currentTarget.src = newSrc;
                        } catch (error) {
                          e.currentTarget.src = "https://placehold.co/200x200?text=Error";
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newImages = [...(currentListing.images || [])];
                        newImages.splice(index, 1);
                        setCurrentListing({ ...currentListing, images: newImages });
                      }}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : localImages.length === 0 && (
            <p className="text-white/40 text-sm italic">No hay imágenes añadidas</p>
          )}

          {/* Añadir nueva imagen por URL */}
          <div className="flex gap-2">
            <Input
              id="new-image"
              placeholder="URL de la imagen"
              className="bg-[#1A1A1C] border-white/10 focus-visible:ring-[#FCAA0D] text-white"
            />
            <Button
              type="button"
              onClick={() => {
                const input = document.getElementById('new-image') as HTMLInputElement;
                if (input.value) {
                  try {
                    // Validar que sea una URL válida
                    new URL(input.value); // Esto lanzará un error si la URL no es válida

                    // Añadir la URL a la lista de imágenes
                    const newImages = [...(currentListing?.images || []), input.value];
                    setCurrentListing({ ...currentListing, images: newImages });
                    input.value = '';

                    toast({
                      title: "URL añadida",
                      description: "La URL de la imagen se ha añadido correctamente"
                    });
                  } catch (error) {
                    toast({
                      title: "URL no válida",
                      description: "Por favor, introduce una URL válida",
                      variant: "destructive"
                    });
                  }
                }
              }}
              className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-medium whitespace-nowrap"
            >
              Añadir URL
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Label htmlFor="description" className="text-white mb-1.5 block">Descripción *</Label>
        <Textarea
          id="description"
          value={currentListing?.description || ''}
          onChange={(e) => setCurrentListing({ ...currentListing, description: e.target.value })}
          placeholder="Describe el producto, sus características, especificaciones..."
          className="min-h-32 bg-[#1A1A1C] border-white/10 focus-visible:ring-[#FCAA0D] text-white"
        />
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <Label className="text-white">Características</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddFeature}
            className="border-white/10 text-white/70 hover:text-white hover:bg-white/5"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Añadir
          </Button>
        </div>
        {(!currentListing?.features || currentListing.features.length === 0) ? (
          <p className="text-white/40 text-sm italic">No se han añadido características</p>
        ) : (
          <div className="flex flex-wrap gap-2 mt-2">
            {currentListing.features.map((feature, index) => (
              <Badge key={index} variant="outline" className="px-2 py-1 bg-[#1A1A1C] border-white/10 text-white/80 flex items-center gap-1.5">
                <Tag className="h-3 w-3 text-[#FCAA0D]" />
                {feature}
                <button
                  type="button"
                  onClick={() => handleRemoveFeature(index)}
                  className="ml-1 text-white/40 hover:text-white/70"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <Label className="text-white">Especificaciones técnicas</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddTechnicalSpec}
            className="border-white/10 text-white/70 hover:text-white hover:bg-white/5"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Añadir
          </Button>
        </div>
        {(!currentListing?.technical_specs || currentListing.technical_specs.length === 0) ? (
          <p className="text-white/40 text-sm italic">No se han añadido especificaciones técnicas</p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {currentListing.technical_specs.map((spec, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={spec.label}
                  onChange={(e) => {
                    const updatedSpecs = [...(currentListing.technical_specs || [])];
                    updatedSpecs[index] = { ...updatedSpecs[index], label: e.target.value };
                    setCurrentListing({ ...currentListing, technical_specs: updatedSpecs });
                  }}
                  placeholder="Etiqueta (ej: Peso)"
                  className="bg-[#1A1A1C] border-white/10 focus-visible:ring-[#FCAA0D] text-white"
                />
                <Input
                  value={spec.value}
                  onChange={(e) => {
                    const updatedSpecs = [...(currentListing.technical_specs || [])];
                    updatedSpecs[index] = { ...updatedSpecs[index], value: e.target.value };
                    setCurrentListing({ ...currentListing, technical_specs: updatedSpecs });
                  }}
                  placeholder="Valor (ej: 120kg)"
                  className="bg-[#1A1A1C] border-white/10 focus-visible:ring-[#FCAA0D] text-white"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveTechnicalSpec(index)}
                  className="h-8 w-8 text-white/50 hover:text-white/70"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => onComplete()}
          className="border-white/10 text-white hover:text-white hover:bg-white/5"
        >
          Cancelar
        </Button>
        <Button
          type="button"
          disabled={createMutation.isPending || updateMutation.isPending || uploadingImages}
          onClick={handleSave}
          className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-medium"
        >
          {(createMutation.isPending || updateMutation.isPending || uploadingImages) && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          {uploadingImages
            ? "Subiendo imágenes..."
            : productId
              ? 'Guardar cambios'
              : 'Crear producto'}
        </Button>
      </div>
    </div>
  );
}
